import type { FormEvent } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	useContributionsPageState,
	useContributionsPageData,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { convertToUsdNumber } from '#src/app/shared/utils/currency';

import { updateContributionAmount } from './api/updateContributionAmount';
import { useValidate } from './hooks/useValidate';

export function useSubmit(): (event: FormEvent<HTMLFormElement>) => Promise<void> {
	const { data } = useContributionsPageData();
	const [
		{
			subscription: { id, unsavedAmount, currencyCode: subsCurrencyCode },
		},
		{ handleContributionsUpdateSuccess, setContributionsFormError, setContributionsFormLoading },
	] = useContributionsPageState();
	const validate = useValidate();
	const { translate, amountToBaseUnits } = useI18n();
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const exchangeRate = data.currencyExchangeRate ?? 1;

	return async function submit(event): Promise<void> {
		event.preventDefault();
		setContributionsFormLoading();
		if (!validate()) {
			return;
		}

		const amountInBaseUnits = amountToBaseUnits(unsavedAmount, subsCurrencyCode);
		void track('subs_settings_change_amount_submit', {
			local_amount: unsavedAmount,
			amount: convertToUsdNumber(unsavedAmount, exchangeRate, subsCurrencyCode),
		});
		const response = await updateContributionAmount(
			{ amount: amountInBaseUnits, id, currency: subsCurrencyCode },
			utilityContext,
		);

		if (response.success) {
			handleContributionsUpdateSuccess({
				amount: unsavedAmount,
				message: translate('fe.pages.subscriptions.manage.plan_saved_success'),
			});
			void track('subs_settings_change_amount_success', {
				local_amount: unsavedAmount,
				amount: convertToUsdNumber(unsavedAmount, exchangeRate, subsCurrencyCode),
			});
		} else {
			setContributionsFormError(translate('fe.errors.generic_try_again'));
			void track('subs_settings_change_amount_failure');
		}
	};
}
